import React, { useCallback, useEffect, useState } from 'react';
import t from 'react-translate';
import { useSelector } from 'react-redux';
import { css } from '@emotion/react';

import { useAppDispatch } from 'redux/store';
import { getCurrentUser } from 'redux/selectors/users';
import { TranslationInfo } from 'redux/schemas/models/edit-translations';
import { ComponentType, NLectureComponent } from 'redux/schemas/models/lecture-component';
import { closeEditTranslationComponent, translationUpdate } from 'redux/actions/edit-translations';
import { getLectureComponent } from 'redux/selectors/timeline';
import EditableTitleWithDescriptionStyled from './base/editable-title-and-description-styled';


type EditableProps = {
  editInfo: TranslationInfo,
  containerClass: string,
  onSaveRef: (fn: () => void) => void,
  setSavingDisabled: (boolean) => void,
};

const EditableProfileCompletion = ({
  editInfo,
  containerClass,
  onSaveRef,
  setSavingDisabled,
}: EditableProps) => {
  const lectureComponent = useSelector((state) => (
    getLectureComponent(state, editInfo.id)
  )) as NLectureComponent<ComponentType.PROFILE_COMPLETION>;
  const profileRequirement = useSelector((state) => state.models.profileRequirements[lectureComponent.profileRequirement]);
  const initialState = {
    title: profileRequirement.title,
    description: lectureComponent.content,
  };
  const [activity, setActivity] = useState(initialState);
  const currentUser = useSelector(getCurrentUser);
  const dispatch = useAppDispatch();

  const editableProfileStyle = css`
    width: 100%;
  `;

  const save = useCallback(() => {
    const { id } = editInfo;
    const attributes = [];
    const fields = Object.keys(activity);
    fields.forEach(field => {
      if (activity[field] !== initialState[field]) {
        const name = field === 'description' ? 'content' : field;
        const elementToUpdate = name === 'content' ? lectureComponent : profileRequirement;
        const elementId = name === 'content' ? id : profileRequirement.id;
        attributes.push({
          translationId: elementToUpdate.translationIds[name],
          name,
          text: activity[field],
          elementType: ComponentType.PROFILE_COMPLETION,
          elementId,
        });
      }
    });
    const payload = {
      id,
      attributes,
    };
    dispatch(translationUpdate(payload)).then(() => {
      dispatch(closeEditTranslationComponent());
    });
  }, [activity, currentUser.translationPreferenceLanguage, dispatch, editInfo]);

  useEffect(() => {
    onSaveRef(() => save);
  }, [onSaveRef, save]);

  useEffect(() => {
    if (initialState !== activity) {
      setSavingDisabled(false);
    }
    return (() => {
      setSavingDisabled(true);
    });
  }, [activity]);


  return (
    <div className='editable-profile' css={editableProfileStyle}>
      <EditableTitleWithDescriptionStyled
        activity={activity}
        setActivity={setActivity}
        containerClass={containerClass}
        titleName={t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.TITLE()}
        descriptionName={t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.EXPECTATIONS()}
      />
    </div>
  );
};

export default EditableProfileCompletion;
